import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "preact/jsx-runtime";
import { useState, useEffect, useRef } from 'preact/hooks';
import { IntegrationProvider, IntegrationAuthType } from '@/types/integrations';
import { integrationsApi } from '@/api/integrations';
import { useToast } from '@/hooks/useToast';
import { useResetConnection } from '@/hooks/useResetConnection';
import { useIntegrationForm } from '@/hooks/useIntegrationForm';
import { ConfirmationModal } from '@/components';
import { IntegrationPanelFooter } from './IntegrationPanelFooter';
import { IntegrationPanelBody } from './IntegrationPanelBody';
import { OneTimeApiKeyModal } from './OneTimeApiKeyModal';
export function IntegrationPanel({ isOpen, clientCompanyId, selectedProvider, managingConnection, allProviders, onClose, onConnectionCreated, onConnectionUpdated, onConnectionDeleted, onConnectionArchived, }) {
    const [creating, setCreating] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [archiving, setArchiving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    // One-time reveal modal state. The plaintext key is only held here for the
    // lifetime of the modal and cleared when the user dismisses it.
    const [oneTimeKey, setOneTimeKey] = useState(null);
    const [oneTimeKeyTitle, setOneTimeKeyTitle] = useState('Save your API key');
    const [pendingConnectionCreated, setPendingConnectionCreated] = useState(null);
    // Local cancellation ref used by create/update/archive/delete flows. Previously
    // supplied by useApiKeyManagement; now managed inline since that hook is gone.
    const cleanupRef = useRef(null);
    const { showSuccess, showInfo, showError } = useToast();
    const { formValues, setFormValues, fieldErrors, setFieldErrors, businessErrors, setBusinessErrors, createError, setCreateError, validate, handlePanelError, resetFormState, nameInputRef, } = useIntegrationForm({ selectedProvider, managingConnection });
    const { resetting, showResetConfirm, setShowResetConfirm, resetConnection, clearResetState, } = useResetConnection({
        clientCompanyId,
        connectionId: managingConnection?.Id,
        onConnectionUpdated,
        onError: (errorMessage) => handlePanelError(errorMessage),
        onKeyRotated: (plaintextKey) => {
            setOneTimeKeyTitle('Your new API key');
            setOneTimeKey(plaintextKey);
        },
    });
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (cleanupRef.current) {
                cleanupRef.current();
                cleanupRef.current = null;
            }
        };
    }, []);
    const handleClose = () => {
        // Cancel any in-flight requests
        if (cleanupRef.current) {
            cleanupRef.current();
            cleanupRef.current = null;
        }
        // Reset all state
        resetFormState();
        setCreating(false);
        setUpdating(false);
        setArchiving(false);
        setDeleting(false);
        setShowDeleteConfirm(false);
        clearResetState();
        setOneTimeKey(null);
        setPendingConnectionCreated(null);
        onClose();
    };
    const createConnection = async () => {
        if (!clientCompanyId || !selectedProvider)
            return;
        const errs = validate();
        if (Object.keys(errs).length > 0) {
            if (errs.Name && nameInputRef.current)
                nameInputRef.current.focus();
            setCreateError('Please fix the highlighted errors.');
            setBusinessErrors([]);
            return;
        }
        setCreating(true);
        setCreateError(null);
        setBusinessErrors([]);
        setFieldErrors({});
        let cancelled = false;
        cleanupRef.current = () => {
            cancelled = true;
        };
        try {
            const payload = {
                ClientCompanyId: clientCompanyId,
                Provider: selectedProvider.id,
                Name: formValues.Name.trim(),
                Description: formValues.Description?.trim() || undefined,
                AuthType: IntegrationAuthType.ApiKey,
                ApiKey: formValues.ApiKey.trim(),
                Channel: selectedProvider.id === IntegrationProvider.BigMarker ? formValues.Channel.trim() : undefined,
            };
            const res = await integrationsApi.createIntegrationConnection(clientCompanyId, payload);
            if (cancelled)
                return;
            if (res.error) {
                handlePanelError(res.error);
                return;
            }
            if (res.data) {
                // Only Webhook creates reveal a plaintext key — for third-party providers
                // the customer supplied their own key and has no need to see it echoed
                // back. Guard by provider (not just by presence of ApiKey) so a future
                // server change can't accidentally resurface the modal.
                const plaintextKey = selectedProvider.id === IntegrationProvider.Webhook ? res.data.ApiKey : undefined;
                // Strip plaintext key from the object we pass to parent state — it
                // must not be persisted beyond the one-time reveal modal.
                const { ApiKey: _stripped, ...viewOnly } = res.data;
                void _stripped;
                if (plaintextKey) {
                    // Show the one-time reveal modal before closing the panel. The
                    // parent's onConnectionCreated fires when the user acknowledges.
                    setOneTimeKeyTitle('Your new API key');
                    setOneTimeKey(plaintextKey);
                    setPendingConnectionCreated(viewOnly);
                    showSuccess('Integration Connected', 'The connection was created successfully.');
                }
                else {
                    onConnectionCreated(viewOnly);
                    showSuccess('Integration Connected', 'The connection was created successfully.');
                    handleClose();
                }
            }
        }
        catch (error) {
            if (!cancelled) {
                setCreateError('Failed to create integration. Please try again.');
            }
        }
        finally {
            if (!cancelled) {
                setCreating(false);
                cleanupRef.current = null;
            }
        }
    };
    const hasManageChanges = () => {
        if (!managingConnection)
            return false;
        const nameChanged = formValues.Name.trim() !== (managingConnection.Name || '');
        const descChanged = formValues.Description.trim() !== (managingConnection.Description || '');
        const keyEntered = !!formValues.ApiKey.trim();
        const channelChanged = formValues.Channel.trim() !== (managingConnection.Channel || '').trim();
        return nameChanged || descChanged || keyEntered || channelChanged;
    };
    const updateConnection = async () => {
        if (!clientCompanyId || !managingConnection)
            return;
        const errs = validate();
        if (errs.Name && !formValues.Name.trim())
            delete errs.Name;
        if (Object.keys(errs).length > 0) {
            setFieldErrors(errs);
            setCreateError('Please fix the highlighted errors.');
            setBusinessErrors([]);
            return;
        }
        if (!hasManageChanges()) {
            setCreateError('No changes to save.');
            setBusinessErrors([]);
            return;
        }
        setUpdating(true);
        setCreateError(null);
        setBusinessErrors([]);
        let cancelled = false;
        cleanupRef.current = () => {
            cancelled = true;
        };
        try {
            const dto = {
                Id: managingConnection.Id,
                ClientCompanyId: clientCompanyId,
                Name: formValues.Name.trim() !== (managingConnection.Name || '') ? formValues.Name.trim() : undefined,
                Description: formValues.Description.trim() !== (managingConnection.Description || '') ? formValues.Description.trim() : undefined,
                ApiKey: formValues.ApiKey.trim() ? formValues.ApiKey.trim() : undefined,
            };
            if (managingConnection.Provider === IntegrationProvider.BigMarker) {
                const currentChannel = (managingConnection.Channel || '').trim();
                const newChannel = formValues.Channel.trim();
                if (newChannel !== currentChannel) {
                    dto.Channel = newChannel || null;
                }
            }
            const res = await integrationsApi.updateIntegrationConnection(clientCompanyId, managingConnection.Id, dto);
            if (cancelled)
                return;
            if (res.error) {
                handlePanelError(res.error);
                return;
            }
            onConnectionUpdated(managingConnection.Id, {
                Name: dto.Name,
                Description: dto.Description,
                Channel: dto.Channel !== undefined ? dto.Channel : undefined,
                UpdatedUtc: new Date().toISOString(),
            });
            setFormValues(f => ({ ...f, ApiKey: '' }));
            showSuccess('Changes Saved', 'Your updates have been applied.');
        }
        catch (error) {
            if (!cancelled) {
                setCreateError('Failed to update integration. Please try again.');
            }
        }
        finally {
            if (!cancelled) {
                setUpdating(false);
                cleanupRef.current = null;
            }
        }
    };
    const toggleArchive = async () => {
        if (!clientCompanyId || !managingConnection)
            return;
        setArchiving(true);
        setCreateError(null);
        setBusinessErrors([]);
        let cancelled = false;
        cleanupRef.current = () => {
            cancelled = true;
        };
        try {
            const isArchived = managingConnection.IsArchived === true;
            const dto = {
                Id: managingConnection.Id,
                ClientCompanyId: clientCompanyId,
                IsArchived: !isArchived,
            };
            const res = await integrationsApi.updateIntegrationConnection(clientCompanyId, managingConnection.Id, dto);
            if (cancelled)
                return;
            if (res.error) {
                handlePanelError(res.error);
                return;
            }
            onConnectionArchived(managingConnection.Id, !isArchived);
            if (!isArchived) {
                showInfo('Connection Archived', 'The connection was archived. You can restore it anytime.');
            }
            else {
                showSuccess('Connection Restored', 'The connection has been reactivated.');
            }
        }
        catch (error) {
            if (!cancelled) {
                showError('Archive Failed', 'Failed to archive connection.');
            }
        }
        finally {
            if (!cancelled) {
                setArchiving(false);
                cleanupRef.current = null;
            }
        }
    };
    const deleteConnection = async () => {
        if (!clientCompanyId || !managingConnection)
            return;
        setDeleting(true);
        setCreateError(null);
        setBusinessErrors([]);
        let cancelled = false;
        cleanupRef.current = () => {
            cancelled = true;
        };
        try {
            const res = await integrationsApi.deleteIntegrationConnection(clientCompanyId, managingConnection.Id);
            if (cancelled)
                return;
            if (res.error) {
                showError('Delete Failed', res.error);
                handlePanelError(res.error);
                return;
            }
            onConnectionDeleted(managingConnection.Id);
            showSuccess('Integration Deleted', 'The connection has been permanently removed.');
            handleClose();
        }
        catch (error) {
            if (!cancelled) {
                showError('Delete Failed', 'Failed to delete connection.');
            }
        }
        finally {
            if (!cancelled) {
                setDeleting(false);
                setShowDeleteConfirm(false);
                cleanupRef.current = null;
            }
        }
    };
    if (!isOpen)
        return null;
    const activeProviderMeta = selectedProvider;
    const providerMeta = managingConnection ? allProviders.find(p => p.id === managingConnection.Provider ||
        p.id.toLowerCase() === (managingConnection.Provider || '').toLowerCase()) : null;
    return (_jsxs("div", { className: "fixed inset-0 z-40", children: [_jsx("div", { className: "absolute inset-0 bg-slate-900/30 backdrop-blur-sm opacity-100 animate-fade-in", onClick: () => { if (!creating && !updating && !archiving && !deleting && !resetting)
                    handleClose(); } }), _jsxs("div", { className: "absolute inset-y-0 right-0 w-full max-w-xl md:max-w-2xl lg:max-w-[50vw] shadow-xl bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-700 flex flex-col animate-slide-in", children: [_jsxs("div", { className: "px-6 py-5 flex items-start justify-between gap-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-800/50", children: [_jsxs("div", { className: "flex items-center gap-3", children: [activeProviderMeta && (_jsx("div", { className: "w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-md flex items-center justify-center overflow-hidden text-slate-700 dark:text-slate-200", children: activeProviderMeta.Icon
                                            ? _jsx(activeProviderMeta.Icon, { className: "w-6 h-6" })
                                            : _jsx("img", { src: activeProviderMeta.logo, alt: activeProviderMeta.title, className: "max-h-8 max-w-8 object-contain" }) })), providerMeta && (_jsx("div", { className: "w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-md flex items-center justify-center overflow-hidden text-slate-700 dark:text-slate-200", children: providerMeta.Icon
                                            ? _jsx(providerMeta.Icon, { className: "w-6 h-6" })
                                            : _jsx("img", { src: providerMeta.logo, alt: providerMeta.title, className: "max-h-8 max-w-8 object-contain" }) })), _jsxs("div", { children: [_jsx("h4", { className: "text-slate-900 dark:text-white font-semibold text-lg leading-tight", children: activeProviderMeta ? `${activeProviderMeta.title} Connection` : (managingConnection?.Name || 'Manage Connection') }), _jsx("p", { className: "text-xs text-slate-500 dark:text-slate-400", children: activeProviderMeta ? 'Configure credentials to establish the connection.' : 'Connection details and maintenance.' })] })] }), _jsx("button", { onClick: handleClose, className: "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 text-sm", disabled: creating || updating || archiving || deleting || resetting, "aria-label": "Close panel", children: "Close" })] }), _jsx(IntegrationPanelBody, { formValues: formValues, setFormValues: setFormValues, fieldErrors: fieldErrors, businessErrors: businessErrors, createError: createError, validate: validate, nameInputRef: nameInputRef, activeProviderMeta: activeProviderMeta, managingConnection: managingConnection, anyBusy: creating || updating || archiving || deleting || resetting, creating: creating, updating: updating, archiving: archiving, deleting: deleting, resetting: resetting, onRotateKey: () => setShowResetConfirm(true), clientCompanyId: clientCompanyId, onSummaryFetched: managingConnection
                            ? (summary) => onConnectionUpdated(managingConnection.Id, { AiSummary: summary })
                            : undefined }), _jsx(IntegrationPanelFooter, { managingConnection: managingConnection, activeProviderMeta: activeProviderMeta, creating: creating, updating: updating, archiving: archiving, deleting: deleting, resetting: resetting, hasManageChanges: hasManageChanges(), onClose: handleClose, onDelete: () => setShowDeleteConfirm(true), onArchiveToggle: toggleArchive, onReset: () => setShowResetConfirm(true), onCreate: createConnection, onUpdate: updateConnection }), managingConnection && (_jsxs(_Fragment, { children: [_jsx(ConfirmationModal, { isOpen: showDeleteConfirm, title: "Delete Integration?", message: `This will permanently remove the '${managingConnection.Name || 'integration'}' connection. This action cannot be undone.`, type: "danger", confirmText: "Delete", cancelText: "Cancel", onConfirm: deleteConnection, onCancel: () => { if (!deleting)
                                    setShowDeleteConfirm(false); }, loading: deleting }), _jsx(ConfirmationModal, { isOpen: showResetConfirm, title: managingConnection.Provider === IntegrationProvider.Webhook ? 'Rotate API key?' : 'Reset Integration', message: managingConnection.Provider === IntegrationProvider.Webhook
                                    ? 'This will generate a new API key and invalidate the current one immediately. Any caller still using the old key will start getting 401 errors. The new key will be shown once — copy it somewhere safe before closing the dialog.'
                                    : 'This will clear the error state and reactivate the integration. The next sync cycle will retry automatically.', type: managingConnection.Provider === IntegrationProvider.Webhook ? 'danger' : 'warning', confirmText: managingConnection.Provider === IntegrationProvider.Webhook ? 'Rotate key' : 'Reset', cancelText: "Cancel", onConfirm: () => { setCreateError(null); setBusinessErrors([]); resetConnection(); }, onCancel: () => { if (!resetting)
                                    setShowResetConfirm(false); }, loading: resetting })] })), _jsx(OneTimeApiKeyModal, { isOpen: oneTimeKey !== null, apiKey: oneTimeKey, title: oneTimeKeyTitle, onAcknowledge: () => {
                            // Clear the key from component state before anything else —
                            // the modal won't show it again, and we don't want it lingering
                            // in React devtools or heap snapshots any longer than necessary.
                            setOneTimeKey(null);
                            // If this reveal came from a create, finalize the connection
                            // creation with the parent now that the user has acknowledged.
                            if (pendingConnectionCreated) {
                                onConnectionCreated(pendingConnectionCreated);
                                setPendingConnectionCreated(null);
                                handleClose();
                            }
                        } })] })] }));
}
